import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  adminMsg,
  omaElisaClassicMsg,
  paaTivaMsg,
  selectAllMsg,
  serviceAgreementMsg,
  t,
  tivaMsg,
} from '../../common/i18n/index.js';
import { isBeingInvited } from '../../common/utils/contactUtils.js';
import { OecAdditionalServiceRoleEnum } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { InvitationStatusCell } from './AccessRightsTable.js';
import type { CLTableColumn } from '../../common/types/componentLibrary.js';
import type { ContactInvitation } from '../../generated/api/contactInvitation.js';

enum ClassicAccessRightsTableField {
  SERVICE_AGREEMENT = 'serviceAgreement',
  ADMIN = 'admin',
  PAA_TIVA = 'paaTiva',
  TIVA = 'tiva',
}

const Checkmark = ({ ariaLabel }: { ariaLabel: string }) => (
  <CL.Icon icon="check" color="success" type="filled" aria-label={ariaLabel} />
);

interface CheckboxCellProps {
  serviceAgreementId: string;
  invitation?: ContactInvitation;
  isChecked: boolean;
  isDisabled: boolean;
  isEdit: boolean;
  ariaLabel: string;
  onChange: () => void;
}

const CheckboxCell = ({
  serviceAgreementId,
  invitation,
  isChecked,
  isDisabled,
  isEdit,
  ariaLabel,
  onChange,
}: CheckboxCellProps) => {
  if (isEdit) {
    return (
      <CL.Checkbox
        id={serviceAgreementId + ariaLabel + isChecked}
        key={serviceAgreementId + ariaLabel + isChecked}
        checked={isChecked}
        disabled={isDisabled}
        onChange={onChange}
      />
    );
  }
  if (isBeingInvited(invitation) && isChecked) {
    return <InvitationStatusCell invitation={invitation!} />;
  }
  return isChecked ? <Checkmark ariaLabel={ariaLabel} /> : null;
};

const ClassicSubheader = () => (
  <div className={classNames(dsClass.FONT_WEIGHT_400, dsClass.TEXT_XS)}>{t.SLBP(omaElisaClassicMsg)}</div>
);

interface ClassicAccessRightsTableProps {
  invitation?: ContactInvitation;
  isEdit: boolean;
  additionalServiceRole?: string;
  setAdditionalServiceRole: (role?: OecAdditionalServiceRoleEnum) => void;
  selectedServiceAgreementIds: string[];
  addServiceAgreementId: (id: string) => void;
  removeServiceAgreementId: (id: string) => void;
  toggleAllServiceAgreements: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ClassicAccessRightsTable = ({
  invitation,
  isEdit,
  additionalServiceRole,
  setAdditionalServiceRole,
  selectedServiceAgreementIds,
  addServiceAgreementId,
  removeServiceAgreementId,
  toggleAllServiceAgreements,
}: ClassicAccessRightsTableProps) => {
  const { authenticatedUser } = useAuth();
  if (!authenticatedUser) {
    return null;
  }

  const serviceAgreements = authenticatedUser?.oecServiceAgreements?.serviceAgreements ?? [];
  if (serviceAgreements.length === 0) {
    return null;
  }

  const isAdditionalRoleVisible =
    serviceAgreements.length === 1 &&
    authenticatedUser?.oecServiceAgreements?.additionalServiceRole === OecAdditionalServiceRoleEnum.PAA_TIVA;
  const isAdditionalRoleEnabled = isAdditionalRoleVisible && selectedServiceAgreementIds.length > 0;
  const isPaaTivaChecked = additionalServiceRole === OecAdditionalServiceRoleEnum.PAA_TIVA;
  const isTivaChecked = additionalServiceRole === OecAdditionalServiceRoleEnum.TIVA;

  const isAdminChecked = (serviceAgreementId: string) => selectedServiceAgreementIds.includes(serviceAgreementId);

  const showSelectAllCheckbox = isEdit && serviceAgreements.length > 1;
  const headerPaddingClass = showSelectAllCheckbox ? dsClass.PADDING_BOTTOM_6 : '';

  const columns: CLTableColumn[] = [
    {
      key: ClassicAccessRightsTableField.SERVICE_AGREEMENT,
      width: '40%',
      label: <div className={headerPaddingClass}>{t.J7FZ(serviceAgreementMsg)}</div>,
    },
    {
      key: ClassicAccessRightsTableField.ADMIN,
      width: '20%',
      align: 'center',
      label: (
        <>
          <div>{t.E112(adminMsg)}</div>
          <ClassicSubheader />
          {showSelectAllCheckbox && (
            <CL.Checkbox onChange={toggleAllServiceAgreements} label={t.A8VA(selectAllMsg)}></CL.Checkbox>
          )}
        </>
      ),
    },
    {
      key: ClassicAccessRightsTableField.PAA_TIVA,
      width: '20%',
      align: 'center',
      label: isAdditionalRoleVisible ? (
        <>
          <div>{t.CI6O(paaTivaMsg)}</div>
          <ClassicSubheader />
        </>
      ) : undefined,
    },
    {
      key: ClassicAccessRightsTableField.TIVA,
      width: '20%',
      align: 'center',
      label: isAdditionalRoleVisible ? (
        <>
          <div>{t.LG80(tivaMsg)}</div>
          <ClassicSubheader />
        </>
      ) : undefined,
    },
  ];

  const rows =
    serviceAgreements.map(serviceAgreement => {
      return {
        [ClassicAccessRightsTableField.SERVICE_AGREEMENT]: serviceAgreement.saName,
        [ClassicAccessRightsTableField.ADMIN]: (
          <CheckboxCell
            serviceAgreementId={serviceAgreement.saId}
            invitation={invitation}
            isChecked={isAdminChecked(serviceAgreement.saId)}
            isEdit={isEdit}
            isDisabled={false}
            ariaLabel={t.E112(adminMsg)}
            onChange={() =>
              isAdminChecked(serviceAgreement.saId)
                ? removeServiceAgreementId(serviceAgreement.saId)
                : addServiceAgreementId(serviceAgreement.saId)
            }
          />
        ),
        ...(isAdditionalRoleVisible
          ? {
              [ClassicAccessRightsTableField.PAA_TIVA]: (
                <CheckboxCell
                  serviceAgreementId={serviceAgreement.saId}
                  invitation={invitation}
                  isChecked={isPaaTivaChecked}
                  isEdit={isEdit}
                  isDisabled={!isAdditionalRoleEnabled || additionalServiceRole === OecAdditionalServiceRoleEnum.TIVA}
                  ariaLabel={t.CI6O(paaTivaMsg)}
                  onChange={() =>
                    setAdditionalServiceRole(isPaaTivaChecked ? undefined : OecAdditionalServiceRoleEnum.PAA_TIVA)
                  }
                />
              ),
              [ClassicAccessRightsTableField.TIVA]: (
                <CheckboxCell
                  serviceAgreementId={serviceAgreement.saId}
                  invitation={invitation}
                  isChecked={isTivaChecked}
                  isEdit={isEdit}
                  isDisabled={
                    !isAdditionalRoleEnabled || additionalServiceRole === OecAdditionalServiceRoleEnum.PAA_TIVA
                  }
                  ariaLabel={t.LG80(tivaMsg)}
                  onChange={() =>
                    setAdditionalServiceRole(isTivaChecked ? undefined : OecAdditionalServiceRoleEnum.TIVA)
                  }
                />
              ),
            }
          : {}),
      };
    }) ?? [];

  return <CL.Table className={dsClass.MARGIN_TOP_5} columns={columns} rows={rows} tableType="striped" />;
};
